<template>
	<AppContentLayout class="flows-view">
		<div v-if="showHeader" class="table-header-container width-100 display-flex flex-row flex-align-center flex-justify-space-between mb-2">
			<Row class="flex-align-center">
				<v-text-field
					outlined
					single-line
					hide-details
					v-model="searchString"
					@click:clear="onClear"
					ref="search_field_ref"
					@blur="isFocused = false"
					@focus="isFocused = true"
					@keydown.enter="onSearch"
					placeholder="Search feature"
					prepend-inner-icon="mdi-magnify"
					clear-icon="mdi-close-circle-outline"
					class="search-action-text-field width-100"
				>
					<template v-slot:append>
						<AnemicButton :class="{ focus: isFocused }" tile class="search-button" @click="onSearch">Search</AnemicButton>
					</template>
				</v-text-field>
			</Row>
			<div class="display-flex flex-row flex-align-center">
				<div class="mr-3 display-flex flex-row flex-align-center">
					My visibility
					<AppTooltip text="Affects only you. Shows or hides your add-ons and command bar tools." position="left">
						<SIcon class="ml-1 pointer" name="installation-tab-info" width="12" height="12" />
					</AppTooltip>
				</div>
				<div class="ml-3 display-flex flex-row flex-align-center">
					Feature status
					<AppTooltip
						position="left"
						text="Affects everyone with access to the feature.<br />Disabled features do not run when triggered. Their UI elements are hidden."
					>
						<SIcon class="ml-1 pointer" name="installation-tab-info" width="12" height="12" />
					</AppTooltip>
				</div>
			</div>
		</div>

		<div class="lists-container scroller height-100">
			<AppLoader v-if="showLoader" class="flows-list-loader" color="#101840" size="18px" />
			<div v-else class="pr-7">
				<FlowsList
					:flows="filteredFlows"
					:searchString="lastUsedSearchString"
					:isLoading="isLoadingWorkflows || showLoader"
					@newWorkflow="(id) => currentFlows.unshift({ id })"
				>
					<template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
						<slot :name="slot" v-bind="scope" />
					</template>
				</FlowsList>
			</div>

			<div v-intersect="endIntersect" v-if="hasNextPage" class="load-more-empty-space width-100 display-flex flex-centerize">
				<div class="width-100">
					<Loader small v-if="isLoadingWorkflows && currentPage > 1 && !showLoader" />
				</div>
			</div>
		</div>
	</AppContentLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import FlowsList from '../FlowsView/FlowsList';
import { mapGettersShorthand } from '@/store/utils/mappers';
import AppContentLayout from '../AppLayout/AppContentLayout';
import { getWorkflow } from '@/store/modules/workflows/getters';
import { selectedProjectId } from '@/store/modules/selectedProject/getters';

export default {
	name: 'FlowsView',
	props: {
		page: { type: Number, default: 1 },
		showHeader: { type: Boolean, default: true },
		filterBy: { type: Object, default: () => ({}) },
		idsToPrepend: { type: Array, default: () => [] },
		idsToShow: { type: Array }, // this is a semi-local filter, passed prom credentials edit module route, -- different from 'filterBy', which makes a network request
	},
	components: {
		FlowsList,
		AppContentLayout,
	},
	computed: {
		...mapGettersShorthand({ getWorkflow }),
		...mapGetters([selectedProjectId.fullPath]),
		selectedProjectId() {
			return this[selectedProjectId.fullPath];
		},
		filteredFlows() {
			return this.idsToShow?.length > 0
				? this.currentFlowsFromStore.filter((x) => this.idsToShow.includes(`${x.id}`))
				: this.currentFlowsFromStore;
		},
		currentFlowsFromStore() {
			return this.currentFlowsIds.map((flowId) => this.getWorkflow(flowId)).filter((flow) => flow);
		},
		showLoader() {
			return !this.afterOneSecond || (this.isLoadingWorkflows && this.currentPage === 1);
		},
		currentFlowsIds() {
			return [...new Set([...this.idsToPrepend, ...this.currentFlows.map(({ id }) => id)])];
		},
	},
	mounted() {
		this.onMounted();
	},
	methods: {
		onMounted() {
			setTimeout(() => {
				this.afterOneSecond = true;
			}, 1500);
			this.getFlows(this.currentPage);
		},
		...mapActions(['selectedProject/getFlowsForSelectedProject']),
		onScrollToBottom() {
			this.currentPage++;
			this.getFlows(this.currentPage);
		},
		clearState() {
			this.currentFlows = [];
			this.currentPage = this.page;
			this.afterOneSecond = false;
		},
		onClear() {
			this.searchString = '';
			this.clearState();
			this.onMounted();
		},
		onSearch() {
			this.clearState();
			this.onMounted();
		},
		async getFlows(page) {
			this.isLoadingWorkflows = true;
			if (page === 1) {
				this.lastUsedSearchString = this.searchString;
			}
			const flowsData = await this['selectedProject/getFlowsForSelectedProject']({
				page,
				keepResults: true,
				filterBy: { ...this.filterBy, workflow_title: this.lastUsedSearchString },
			});
			this.isLoadingWorkflows = false;
			this.hasNextPage = Boolean(flowsData.next);
			this.currentFlows = [...this.currentFlows, ...flowsData.results];
		},
		endIntersect(entries, observer, isIntersecting) {
			if (isIntersecting) {
				this.onScrollToBottom();
			}
		},
	},
	data() {
		return {
			isFocused: false,
			searchString: '',
			currentFlows: [],
			hasNextPage: false,
			afterOneSecond: false,
			currentPage: this.page,
			lastUsedSearchString: '',
			isLoadingWorkflows: false,
		};
	},
};
</script>

<style scoped lang="scss">
.flows-view {
	.top-section {
		padding: 10px;
		font-size: 16px;
		font-weight: 700;

		.group-by-select {
			width: 300px;
		}
	}
	.lists-container {
		overflow-y: auto;
		padding-bottom: 10px;

		.flows-list-loader {
			height: 100%;
			transform: translate(0, 50%);
		}
	}
}
.load-more-empty-space {
	height: 100px;
}

.scroller {
	overflow: auto;
	color: #c1c1c1;
}
.table-header-container {
	font-size: 12px;
	color: #9e9e9e;
	font-weight: 400;
	padding-right: 113px;
}
.search-action-text-field {
	height: 35px !important;
	width: 385px !important;
	$re-margin-top: 6px;

	::v-deep {
		.v-input__prepend-inner {
			margin-top: $re-margin-top;
			font-size: 8px !important;
			height: 4px !important;
		}
		.v-icon {
			font-size: 16px !important;
		}
		.v-input__append-inner {
			cursor: pointer;
			margin-top: 0px;
			margin-right: -12px !important;

			&.focused {
				.search-button {
					border-color: #101840 !important;
					color: #101840 !important;
				}
			}
		}
	}
}
.search-button {
	height: 35px !important;
	font-size: 14px !important;
	padding: 0px 20px !important;
	color: #727375 !important;
	border-radius: 6px !important;
	background: white !important;
	font-weight: normal !important;
	letter-spacing: 0.21px !important;
	border: 1px solid #cbcbcb !important;
	border-radius: 0px 6px 6px 0px !important;
	&.focus {
		color: #5c8df5 !important;
		border-color: #5c8df5 !important;
	}
}
</style>
